import { Component } from "@theme/component";

export class QuantityPickerComponent extends Component {
  requiredRefs = ["quantitySelectorWrapper"];

  /**
   * Resolves the product form for this product (same section), avoiding the first
   * unrelated product-form on the page (e.g. quick-add).
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    const section = this.closest(".shopify-section");
    if (productId && section?.id?.startsWith("shopify-section-")) {
      const sectionId = section.id.replace("shopify-section-", "");
      return (
        document.querySelector(
          `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`,
        ) ?? null
      );
    }
    return document.querySelector("product-form-component");
  }

  /**
   * Syncs the picked quantity into the product form so the cart-add request
   * uses the correct value. Writes both to the theme's quantity-selector ref
   * (if present) AND to a hidden form input as fallback.
   */
  #syncFormQuantity(productForm, value) {
    const num = Number(value);
    const str = Number.isFinite(num) && num > 0 ? String(Math.floor(num)) : "1";

    const qs = productForm.refs?.quantitySelector;
    if (qs?.setValue) {
      qs.setValue(str);
      qs.updateButtonStates?.();
    }

    const form = productForm.querySelector("form");
    if (!form) return;
    let input = form.querySelector('input[name="quantity"]');
    if (!input) {
      input = document.createElement("input");
      input.type = "hidden";
      input.name = "quantity";
      form.appendChild(input);
    }
    input.value = str;
  }

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(
      "quantity-selector:update",
      this.handleQuantityUpdate.bind(this),
    );

    const options = this.refs.options ?? [];
    options.forEach((option) => {
      option.addEventListener("click", () => {
        const value = parseInt(option.dataset.value, 10);
        if (!Number.isFinite(value)) return;
        this.updateQuantitySelector(value);
        this.updateActiveState(value);
      });
    });
  }

  handleQuantityUpdate(event) {
    const value = event.detail.quantity;
    this.updateActiveState(value);

    const productForm = this.#getProductForm();
    if (productForm) this.#syncFormQuantity(productForm, value);
  }

  updateQuantitySelector(value) {
    const quantitySelector = this.querySelector("quantity-selector-component");
    if (quantitySelector) {
      quantitySelector.setValue(String(value));
      quantitySelector.onQuantityChange();
      quantitySelector.updateButtonStates();
    }

    const productForm = this.#getProductForm();
    if (productForm) this.#syncFormQuantity(productForm, value);
  }

  updateActiveState(value) {
    const options = this.refs.options ?? [];
    options.forEach((option) => {
      const optionValue = parseInt(option.dataset.value);
      const isPlus = option.dataset.plus === "true";

      if (isPlus) {
        if (value >= optionValue) {
          option.classList.add("is-active");
        } else {
          option.classList.remove("is-active");
        }
      } else {
        if (value === optionValue) {
          option.classList.add("is-active");
        } else {
          option.classList.remove("is-active");
        }
      }
    });
  }
}

if (!customElements.get("quantity-picker-component")) {
  customElements.define("quantity-picker-component", QuantityPickerComponent);
}
